import React, { useEffect, useState } from "react";
import { COMPENSATION_TYPES } from "../../constants/timesheetStatus";

export default function ClientModal({
  isOpen,
  onClose,
  onSave,
  client,
  darkMode = false,
}) {
  const [form, setForm] = useState({
    name: "",
    fiscalCode: "",
    vatNumber: "",
    sdiCode: "",
    pecEmail: "",
    contactPerson: "",
    email: "",
    phone: "",
    address: "",
    compensationType: "daily",
    rate: "",
    paymentTermsDays: "30",
    notes: "",
    isActive: true,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (client) {
      setForm({
        name: client.name || "",
        fiscalCode: client.fiscalCode || "",
        vatNumber: client.vatNumber || "",
        sdiCode: client.sdiCode || "",
        pecEmail: client.pecEmail || "",
        contactPerson: client.contactPerson || "",
        email: client.email || "",
        phone: client.phone || "",
        address: client.address || "",
        compensationType: client.compensationType || "daily",
        rate: client.rate ? String(client.rate) : "",
        paymentTermsDays: String(client.paymentTermsDays ?? 30),
        notes: client.notes || "",
        isActive: client.isActive !== false,
      });
    } else {
      setForm({
        name: "",
        fiscalCode: "",
        vatNumber: "",
        sdiCode: "",
        pecEmail: "",
        contactPerson: "",
        email: "",
        phone: "",
        address: "",
        compensationType: "daily",
        rate: "",
        paymentTermsDays: "30",
        notes: "",
        isActive: true,
      });
    }
  }, [isOpen, client]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Il nome del cliente è obbligatorio.");
      return;
    }
    const rate = Number(String(form.rate).replace(",", ".")) || 0;
    if (form.compensationType !== "project" && rate <= 0) {
      alert("Inserisci una tariffa valida (> 0).");
      return;
    }
    onSave({
      ...form,
      rate,
      paymentTermsDays: Number(form.paymentTermsDays) || 30,
    });
  };

  const rateLabel =
    form.compensationType === "daily"
      ? "Tariffa giornaliera (€/gg)"
      : form.compensationType === "hourly"
        ? "Tariffa oraria (€/h)"
        : form.compensationType === "fixed"
          ? "Compenso mensile (€)"
          : null;

  const inputCls = `mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
    darkMode
      ? "border-neutral-700 bg-neutral-800 text-white"
      : "border-neutral-200 bg-white text-neutral-900"
  }`;
  const labelCls = `text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className={`relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl border p-5 ${
          darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
        }`}
      >
        <div className="flex items-start justify-between">
          <h3 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            {client ? "Modifica cliente" : "Nuovo cliente"}
          </h3>
          <button
            className={`p-2 rounded-lg ${darkMode ? "hover:bg-neutral-800 text-neutral-500" : "hover:bg-neutral-100 text-neutral-500"}`}
            onClick={onClose}
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Sezione Anagrafica */}
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
              Anagrafica
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Nome / Ragione sociale *</label>
                <input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Es. ACME Srl"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Codice fiscale</label>
                  <input
                    className={inputCls}
                    value={form.fiscalCode}
                    onChange={(e) => handleChange("fiscalCode", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>Partita IVA</label>
                  <input
                    className={inputCls}
                    value={form.vatNumber}
                    onChange={(e) => handleChange("vatNumber", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Codice SDI</label>
                  <input
                    className={inputCls}
                    value={form.sdiCode}
                    onChange={(e) => handleChange("sdiCode", e.target.value)}
                    maxLength={7}
                  />
                </div>
                <div>
                  <label className={labelCls}>PEC</label>
                  <input
                    type="email"
                    className={inputCls}
                    value={form.pecEmail}
                    onChange={(e) => handleChange("pecEmail", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-100"}`} />

          {/* Sezione Contatti */}
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
              Contatti
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Referente</label>
                <input
                  className={inputCls}
                  value={form.contactPerson}
                  onChange={(e) => handleChange("contactPerson", e.target.value)}
                  placeholder="Nome e cognome"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Email</label>
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefono</label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Indirizzo</label>
                <input
                  className={inputCls}
                  value={form.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-100"}`} />

          {/* Sezione Condizioni */}
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
              Condizioni
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Tipo compenso</label>
                <select
                  className={inputCls}
                  value={form.compensationType}
                  onChange={(e) => handleChange("compensationType", e.target.value)}
                >
                  {COMPENSATION_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              {rateLabel && (
                <div>
                  <label className={labelCls}>{rateLabel}</label>
                  <input
                    className={inputCls}
                    value={form.rate}
                    onChange={(e) => handleChange("rate", e.target.value)}
                    placeholder="0,00"
                  />
                </div>
              )}
              <div>
                <label className={labelCls}>Giorni di pagamento</label>
                <input
                  type="number"
                  className={inputCls}
                  value={form.paymentTermsDays}
                  onChange={(e) => handleChange("paymentTermsDays", e.target.value)}
                  min={0}
                />
              </div>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className={labelCls}>Note</label>
            <textarea
              className={`${inputCls} min-h-[60px]`}
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Note interne..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode
                  ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
              onClick={onClose}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-3 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
            >
              {client ? "Salva" : "Crea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
